import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Rocket, Hammer, Zap, GitPullRequest, Trophy } from "lucide-react";

const DAYS = [
  { day: "Tuesday", title: "Kickoff · Event Starts", desc: "Registration opens. Pick a real problem and start building your own skill.", icon: Rocket },
  { day: "Wednesday", title: "Build Window Opens", desc: "Heads-down coding + prompt engineering. Ship early, ship often.", icon: Hammer },
  { day: "Thursday", title: "Build & Refine", desc: "Keep building. Tighten prompts, wire up tools, test with real cases.", icon: Zap },
  { day: "Friday", title: "Submission Deadline", desc: "Submissions open till Friday. Open a PR to the carestack.skills repo before EOD.", icon: GitPullRequest },
  { day: "Next Week", title: "Judging & Winners 🎉", desc: "Judges score every entry against the rubric. Winners announced.", icon: Trophy },
];

function TimelineNode({ item, index, progress }) {
  const start = index / DAYS.length;
  const scale = useTransform(progress, [start - 0.05, start + 0.05], [0.6, 1]);
  const opacity = useTransform(progress, [start - 0.05, start + 0.05], [0.3, 1]);
  const glow = useTransform(progress, [start - 0.05, start + 0.05], [0, 1]);
  const boxShadow = useTransform(
    glow,
    [0, 1],
    ["0 0 0px rgba(47,204,124,0)", "0 0 24px rgba(47,204,124,0.7)"]
  );

  const Icon = item.icon;

  return (
    <div className="relative grid grid-cols-[80px_1fr] md:grid-cols-[120px_60px_1fr] gap-4 md:gap-8 items-start" data-testid={`timeline-node-${index}`}>
      {/* Day label */}
      <motion.div style={{ opacity }} className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-zinc-500 pt-4 md:text-right">
        {item.day}
      </motion.div>

      {/* Node marker */}
      <div className="relative justify-center pt-4 hidden md:flex">
        <motion.div
          style={{ scale, boxShadow }}
          className="relative z-10 w-5 h-5 rounded-full bg-[#050505] border-2 border-[#2FCC7C] flex items-center justify-center"
        >
          <motion.span style={{ opacity: glow }} className="w-2 h-2 rounded-full bg-[#2FCC7C]" />
        </motion.div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass rounded-2xl p-5 md:p-6 hover:border-[#2FCC7C]/40 transition-colors"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2FCC7C]/20 to-[#2D7BF0]/20 border border-[#2FCC7C]/30 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-[#2FCC7C]" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight">
              {item.title}
            </h3>
            <p className="mt-1.5 text-sm md:text-base text-zinc-400 leading-relaxed">
              {item.desc}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Timeline() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 0.85", "end 0.35"],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="timeline"
      className="relative py-16 lg:py-20 px-6 md:px-12 lg:px-24"
      data-testid="timeline-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#2FCC7C] mb-4">
            // 02 · Timeline
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-tight">
            <span className="text-gradient-neon">Tuesday</span> to{" "}
            <span className="text-gradient-neon">Friday</span>.
          </h2>
          <p className="mt-6 text-lg text-zinc-400">
            Kicks off Tuesday, submissions stay open till Friday. Scroll to activate the timeline.
          </p>
        </motion.div>

        <div ref={containerRef} className="relative" data-testid="timeline-container">
          {/* Vertical rail (desktop) */}
          <div className="absolute left-[calc(120px+30px-1px)] top-0 bottom-0 w-[2px] bg-white/5 hidden md:block">
            <motion.div
              style={{ height: lineHeight }}
              className="w-full bg-gradient-to-b from-[#2FCC7C] via-[#2FCC7C] to-[#2D7BF0]"
            />
          </div>

          <div className="space-y-6 md:space-y-8">
            {DAYS.map((d, i) => (
              <TimelineNode key={i} item={d} index={i} progress={scrollYProgress} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
